import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';

interface StepIndicatorProps {
  labels: string[];
  activeStep: number;
  /**
   * Called with the index of a completed step the user clicked, so they can
   * jump back to it. Only steps before `activeStep` are clickable — a form
   * cannot be skipped forwards.
   */
  onBack?: (step: number) => void;
}

export const StepIndicator = ({
  labels,
  activeStep,
  onBack,
}: StepIndicatorProps) => {
  const canGoBackTo = (i: number) => onBack !== undefined && i < activeStep;
  const backHandler = (i: number) =>
    canGoBackTo(i) ? () => onBack?.(i) : undefined;

  const circleClass = (i: number) =>
    classNames(
      'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 text-sm',
      {
        'bg-primary border-primary font-bold text-white': i < activeStep,
        'border-sky-800 bg-white text-sky-800': i === activeStep,
        'border-gray-300 bg-white text-gray-400': i > activeStep,
      },
    );

  const labelClass = (i: number) =>
    classNames('text-sm font-medium', { 'text-sky-800': i === activeStep });

  return (
    <>
      {/* Mobile vertical */}
      <div className="mb-10 flex flex-col gap-0 sm:hidden">
        {labels.map((label, i) => (
          <div
            key={i}
            onClick={backHandler(i)}
            className={classNames(
              'flex items-start',
              canGoBackTo(i) && 'cursor-pointer',
            )}
          >
            <div className="flex flex-col items-center">
              <div className={circleClass(i)}>
                {i < activeStep ? '✓' : i + 1}
              </div>
              {i < labels.length - 1 && (
                <div
                  className={classNames(
                    'h-4 w-0.5',
                    i < activeStep ? 'bg-primary' : 'bg-card-border',
                  )}
                />
              )}
            </div>
            <span className={classNames('mt-1.5 ml-3', labelClass(i))}>
              {label}
            </span>
          </div>
        ))}
      </div>

      {/* Desktop horizontal */}
      <div className="mb-10 hidden w-full sm:flex">
        {labels.map((label, i) => (
          <div
            key={i}
            onClick={backHandler(i)}
            className={classNames(
              'relative flex flex-1 flex-col items-center',
              canGoBackTo(i) && 'cursor-pointer',
            )}
          >
            {i > 0 && (
              <div
                className={classNames(
                  'absolute top-4 right-1/2 left-0 h-0.5 -translate-y-px',
                  i <= activeStep ? 'bg-primary' : 'bg-card-border',
                )}
              />
            )}
            {i < labels.length - 1 && (
              <div
                className={classNames(
                  'absolute top-4 right-0 left-1/2 h-0.5 -translate-y-px',
                  i < activeStep ? 'bg-primary' : 'bg-card-border',
                )}
              />
            )}
            <div className={classNames('relative z-10', circleClass(i))}>
              {i < activeStep ? '✓' : i + 1}
            </div>
            <span
              className={classNames('mt-2 text-center text-sm', labelClass(i))}
            >
              {label}
            </span>
          </div>
        ))}
      </div>
    </>
  );
};
